<template>
  <div>

    <table-tools :actions="tableActions">

      <table-filter>
        <o-text-field
          :label="$t('titles.venue_name')"
          v-model="filterForm.name"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.channel')"
          v-model="filterForm.channel"
          :items="channelOptions"
          clearable
        />
      </table-filter>

      <table-filter>
        <v-checkbox
          :label="filterForm.liveOnly ? $t('titles.hide_non_live_venues_n_hidden', venues.length - filteredVenues.length) : $t('titles.hide_non_live_venues')"
          v-model="filterForm.liveOnly"
        />
      </table-filter>

    </table-tools>

    <v-data-table
      :headers="headers"
      :items="filteredVenues"
      :sort-by="sortBy"
      items-per-page="-1"
      :loading="loading"
      :no-data-text="$t('alerts.no_location_venues_found')"
      :show-select="canWriteVlps"
      item-value="ID"
      v-model="selectedVenueIDs"
    >

      <template v-slot:item.name="{ item }">

        {{ item.name }}
        <div v-if="item.formattedAddress" class="text-grey-darken-1">{{ item.formattedAddress }}</div>

      </template>

      <template v-slot:item.channels="{ item }">

        <template v-for="channelName, i in item.channels">
          <template v-if="i > 0">, </template>
          {{ ChannelRegistry.find(channelName)?.titleShort ?? channelName }}
        </template>

      </template>

      <template
        v-for="lp in eventLocationProducts"
        v-slot:[`item.vlpForLp${lp.ID}`]="{ item }"
      >

        <v-card
          v-for="vlp in item.venueLocationProducts.filter((vlp) => vlp.locationProductID === lp.ID)"
          class="my-8"
        >

          <template v-slot:text>

            <div v-if="canSeeIds" class="text-grey-darken-2 text-body-2 text-right mb-4">
              VLP {{ vlp.ID }}
            </div>

            <v-row dense>

              <v-col>
                {{ $t('titles.before_event_colon') }}
                {{ formatTime(vlp.entryHoursBeforeEvent) }}
              </v-col>

              <v-col>
                {{ $t('titles.after_event_colon') }}
                {{ formatTime(vlp.exitHoursAfterEvent) }}
              </v-col>

            </v-row>

            <v-row dense>

              <v-col>
                {{ $t('titles.default_inventory_colon') }}
                {{ vlp.defaultInventory }}
              </v-col>

              <v-col>
        
                <template v-for="venueTier in vlp.venueTiers">
                  {{ currencyFormatter.format(venueTier.tierPrice) }} up to
                  {{ Math.floor(venueTier.tierInventoryPercentage * 100) }}%<br>
                </template>

              </v-col>

            </v-row>

          </template>

          <template v-slot:actions>
            <v-row>

              <v-col>
                <span :class="vlp.live ? 'text-enabled' : 'text-disabled'">
                  <v-icon :icon="vlp.live ? '$enabled' : '$disabled'"></v-icon>
                  {{ vlp.live ? 'Live' : 'Not Live' }}
                </span>
              </v-col>

              <v-col class="text-end">
                <v-btn
                  density="compact"
                  :prepend-icon="canWriteVlps ? '$edit' : '$view'"
                  :text="canWriteVlps ? $t('actions.edit') : $t('actions.view')"
                  @click="openSingleEditModal(vlp)"
                />
              </v-col>

            </v-row>
          </template>

        </v-card>
        
      </template>

      <template v-slot:bottom>
        <!-- This is just here to hide the pagination controls -->
      </template>

    </v-data-table>

  </div>
</template>

<script setup>

import { computed, inject, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

// components
import VlpEditModal from './VlpEditModal.vue'

// models
import Venue from '../../../models/Venue'
import Location from '../../../models/Location'
import LocationProduct from '../../../models/LocationProduct'

// misc
import ChannelRegistry from '../../../channels/ChannelRegistry'
import { currencyFormatter } from '../../../utils/formatters'
import NotificationService from '../../../services/NotificationService'
import { openDialog } from 'vue3-promise-dialog'
import {useTitle} from '@vueuse/core'

const props = defineProps({

  location: {
    type: Location,
    required: true,
  },

})

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.location_venues', {location_name: props.location.locationName})

const $can = inject('$can')
const canSeeIds = $can('seeids')
const canWriteVlps = $can('vlps.write')

/**
 * Initialization
 */

const loading = ref(false)

// onBeforeMount(() => {
onMounted(() => {

  loading.value = true

  const promises = []

  promises.push(
    promises.push(
      LocationProduct.fetchByLocationID(props.location.ID)
        .catch((e) => {
        console.error(e)
        NotificationService.error('Couldn\'t load location products!')
      })
    )
  )

  promises.push(
    Venue.fetchLocationVenues(props.location.ID)
      .catch((e) => {
        console.error(e)
        NotificationService.error('Couldn\'t load venues!')
      })
  )

  Promise.all(promises).finally(() => {
    loading.value = false
  })

})

/**
 * Filter form
 */

const tableActions = computed(() => [
  {
    title: t('actions.edit_selected'),
    icon: '$edit',
    disabled: !atLeastOneVenueIsSelected.value,
    click: openBulkEditModal,
    hidden: !canWriteVlps,
  },
])

const filterForm = reactive({
  name: null,
  channel: null,
  liveOnly: true,
})

const channelOptions = computed(() => {

  const channels = new Set()

  venues.value.forEach((venue /*: Venue*/) => {
    venue.channels.forEach((channel /*: string*/) => {
      channels.add(channel)  
    })    
  })

  const options = Array.from(channels).map((channel /*:string*/ ) => {
    return {
      value: channel,
      title: ChannelRegistry.find(channel)?.titleShort ?? channel,
    }
  })

  return options

})

/**
 * Table headers
 */

const headers = computed(() => {

  const output = []

  if (canSeeIds) {
    output.push({key: 'ID', title: 'ID', hidden: canSeeIds})
  }

  output.push({key: 'name', title: 'Venue'})
  output.push({key: 'channels', title: 'Channels'})

  eventLocationProducts.value.forEach((locationProduct) => {
    output.push({
      title: locationProduct.product.name + (canSeeIds ? ` (${locationProduct.ID})` : ''),
      key: 'vlpForLp' + locationProduct.ID,
      sortable: false,
      maxWidth: 300,
    })
  })

  return output

})

const sortBy = ref([{key: 'name'}])

/**
 * Table data
 */

const locationProductIDs = computed(() => {
  return LocationProduct.query()
    .where('locationID', props.location.ID)
    .whereHas('product', (query) => {
      query.where('verticalType', 'event')
    })
    .get()
    .map((lp) => lp.ID)
})

const venues = computed(() => {
  const output = Venue.query()
    .with('venueLocationProducts', (query) => {
      query.where('locationProductID', (locationProductID) => {
        return locationProductIDs.value.includes(locationProductID)
      })
    })
    .with('venueLocationProducts.venueTiers', (query) => {
      query.orderBy('tierInventoryPercentage')
    })
    .whereHas('venueLocationProducts', (query) => {
      query.where('locationProductID', (locationProductID) => {
        return locationProductIDs.value.includes(locationProductID)
      })
    })
    .get()
  return output
})

const filteredVenues = computed(() => {

  let nameFilter = null
  if (filterForm.name !== null && filterForm.name !== undefined) {
    const trimmed = filterForm.name.trim()
    if (trimmed.length > 0) {
      nameFilter = trimmed.toUpperCase()
    }
  }

  return venues.value
    .filter((venue /*:Venue*/) => {

      if (nameFilter !== null && venue.name.toUpperCase().indexOf(nameFilter) === -1) {
        return false
      }

      if (filterForm.channel && !venue.channels.includes(filterForm.channel)) {
        return false
      }

      if (filterForm.liveOnly) {
        if (!venue.venueLocationProducts.some((vlp) => vlp.live)) {
          return false
        }
      }

      return true

    })

})

const eventLocationProducts = computed(() => {
  return LocationProduct.query()
    .with(['product'])
    .where('locationID', props.location.ID)
    .whereHas('product', (query) => {
      query.where('verticalType', 'event')
    })
    .all()
})

/**
 * Table row selection
 */

const selectedVenueIDs = ref([])
const atLeastOneVenueIsSelected = computed(() => selectedVenueIDs.value.length > 0)

/**
 * Modals
 */

function openSingleEditModal(vlp) {
  openDialog(VlpEditModal, {
    locationID: props.location.ID,
    venueLocationProductIDs: [vlp.ID],
    readonly: !canWriteVlps,
  })
}

function openBulkEditModal() {

  // I tried using VDataTable's return-object prop (so that its v-model would
  // contain the actual venue objects, instead of just their IDs), but that
  // led to buggy behavior. This is a small hack to work around that bugginess.
  const selectedVenues = filteredVenues.value.filter((venue) => {
    return selectedVenueIDs.value.includes(venue.ID)
  })

  const vlps = selectedVenues.reduce((vlps, venue) => {
    return [...vlps, ...venue.venueLocationProducts]
  }, [])

  openDialog(VlpEditModal, {
    locationID: props.location.ID,
    venueLocationProductIDs: vlps.map(vlp => vlp.ID),
  }).then(() => {
    selectedVenueIDs.value = []
  })

}

/**
 * Misc
 */

function formatTime(value) {
  if (value === null) {
    return 'Use product settings'
  }
  const hours = Math.floor(value)
  const minutes = (value % 1) * 60
  let formatted = ''
  if (minutes) {
    formatted = t('misc.duration_hours_minutes', {hours: hours, minutes: minutes})
  } else {
    formatted = t('misc.duration_hours', {hours: hours})
  }
  return formatted
}

</script>
